<script setup>
import { useStore } from 'vuex'
import { computed } from '@vue/reactivity'
import { useRouter } from 'vue-router'
import { Base64 } from 'js-base64'
import axios from 'axios'

const store = useStore()
const router = useRouter()

const prices = computed(() => store.getters['system/getTotalPrice'])
const products = computed(() => store.getters['system/products'])
const images = computed(() => store.getters['system/previewImages'])
const montageCost = computed(() => store.getters['checkout/montageCost'] || 0)
const montageStatus = computed(() => store.getters['checkout/montageStatus'])

function toPrintPrice (value) {
  const price = Number(value)
  return Number.isFinite(price) ? price : 0
}

function normalizePrintImages (images) {
  return (images || [])
    .map((image, index) => {
      if (typeof image === 'string') {
        return { path: image, order: index }
      }
      if (image && typeof image.path === 'string' && image.path) {
        return {
          path: image.path,
          order: typeof image.order === 'number' ? image.order : index,
          option_id: image.option_id || null,
          step_id: image.step_id || null
        }
      }
      return null
    })
    .filter(Boolean)
    .sort((a, b) => (a.order || 0) - (b.order || 0))
}

function resolvePrintImages (item, configKey) {
  const previewGroups = store.getters['system/previewImages'] || []
  const fromPreview = normalizePrintImages(
    previewGroups.find(group => group.config === configKey)?.images
  )
  if (fromPreview.length) {
    return fromPreview
  }

  const system = store.getters['system/system']
  if (configKey && system?.[configKey] && typeof system[configKey].getPreviewImages === 'function') {
    const fromConfig = normalizePrintImages(system[configKey].getPreviewImages())
    if (fromConfig.length) {
      return fromConfig
    }
  }

  return normalizePrintImages(item.images)
}

function buildPrintPayload (item, configKey = null) {
  return {
    id: item.id,
    name: item.name,
    images: resolvePrintImages(item, configKey),
    price: {
      discounted: toPrintPrice(item.price?.discounted),
      total: toPrintPrice(item.price?.total),
      old_price: toPrintPrice(item.price?.total ?? item.price?.old_price)
    },
    steps: (item.steps || []).map(step => ({
      name: step.name,
      value: step.value ?? step.selectedLabel ?? '--',
      price: toPrintPrice(step.price),
      print_description: step.printDescription || step.print_description || null
    }))
  }
}

function buildRequestSummary () {
  const totals = prices.value || { discounted: 0, price: 0 }
  const steps = (store.getters['system/steps'] || [])
    .filter(step => step.type !== 'total')
    .map(step => ({
      name: step.name,
      value: step.selectedLabel || '--',
      option: []
    }))

  return {
    id: store.getters['system/system']?.id,
    name: 'Individueller Zaun-Konfigurator',
    price: {
      discounted: totals.discounted || 0,
      old_price: totals.price || 0,
      total: totals.price || 0
    },
    steps
  }
}

async function printed () {
  const apiKey = btoa(process.env.VUE_APP_API_KEY)
  const apiUrl = process.env.VUE_APP_API_URL
  const p = document.getElementById('print-content')
  if (!p) {
    return
  }

  const summary = store.getters['system/summary'] || {}
  const productItems = summary.products || []
  let html = ''

  try {
    if (productItems.length > 0) {
      for (let i = 0; i < productItems.length; i++) {
        const product = productItems[i]
        const payload = buildPrintPayload(product.summary || {}, product.config || null)
        const config = Base64.encode(JSON.stringify(payload))
        const resp = await axios.post(apiUrl + 'print/configurator', {
          re: apiKey,
          config,
          include_header: i === 0,
          include_footer: i === productItems.length - 1
        })
        if (resp.data) {
          html += resp.data
        }
      }
    } else {
      const payload = buildPrintPayload(buildRequestSummary())
      const config = Base64.encode(JSON.stringify(payload))
      const resp = await axios.post(apiUrl + 'print/configurator', {
        re: apiKey,
        config,
        include_header: true,
        include_footer: true
      })
      html = resp.data || ''
    }

    if (html) {
      p.innerHTML = html
      setTimeout(() => {
        window.print()
      }, 500)
    }
  } catch (error) {
    console.error(error)
  }
}

function addToCart () {
  const summary = store.getters['system/summary'] || {}
  const productItems = summary.products || []

  productItems.forEach(product => {
    const item = product.summary
    if (!item) {
      return
    }
    store.dispatch('checkout/addItem', {
      id: item.id,
      name: item.name,
      images: item.images || [],
      quantity: product.amount || 1,
      price: {
        discounted: item.price?.discounted || 0,
        total: item.price?.discounted || item.price?.total || 0,
        old_price: item.price?.total || 0
      },
      steps: item.steps || []
    })
  })

  setTimeout(() => {
    router.push({ name: 'cart' })
  }, 300)
}

function onChange (e) {
  store.dispatch('system/setMontage', !!e.target.checked)
}
</script>

<template>
  <div class="row">
    <div class="col-md-12">
      <h3>Zusammenfassung - Alles auf einen Blick</h3>
      <div class="table-responsive">
        <table class="table">
          <tbody>
          <tr v-for="(product, index) in products" :key="index">
            <td style="width: 250px">
              <div class="text-center">
                <div class="product-main-image" v-for="(items, index) in images" :key="index">
                  <template v-if="product.config === items.config">
                    <template v-for="(image, i) in items.images" :key="i">
                      <img :src="$filters.image(image.path, '200x')"  />
                    </template>
                  </template>
                </div>
              </div>
            </td>
            <td>
              <p>
                <strong>{{ product.name }}</strong>
              </p>
              <ul>
                <li v-for="(step, index) in product.steps" :key="index">
                  {{ step.name }}: <span class="link">{{ step.selectedLabel }}&nbsp;</span>
                </li>
              </ul>
            </td>
            <td class="text-right">
              <h5><span class="discount-new"><strong>{{ $filters.currency(product.price.total) }}</strong></span></h5>
            </td>
          </tr>
          </tbody>
          <tfoot>
          <tr>
            <td><strong>Gesamtpreis:</strong></td>
            <td>
              <div class="form-check">
                <input type="checkbox" class="form-check-input" id="montage" :checked="montageStatus" @change="onChange">
                <label class="form-check-label" for="montage">
                  Mit Komplettmontage zzg. <span class="text-danger">{{ $filters.currency(montageCost) }}</span>
                </label>
              </div>
            </td>
            <td class="text-right" style="width: 300px">
              <h5 v-if="montageStatus">Montage: <span class="text-danger">{{ $filters.currency(montageCost) }}</span></h5>
              <h5>Gesamt:
                <span class="text-danger" v-if="prices && prices.discounted > 0">{{ $filters.currency(prices.discounted + ((montageStatus) ? montageCost : 0)) }}</span>
                <span class="text-price" :class="{'old-price': prices.discounted > 0}">{{ $filters.currency(prices.price + ((montageStatus) ? montageCost : 0)) }}</span>
              </h5>
            </td>
          </tr>
          <tr>
            <td colspan="3" class="text-right">
              <span class="small-text text-muted">ink. 19% MwSt. & Versandkosten</span>
            </td>
          </tr>
          <tr>
            <td class="text-left">
              <button type="button" class="btn btn-dark" @click="printed">Drucken</button>
            </td>
            <td colspan="2" class="text-right">
              <button type="button" class="btn btn-primary" @click="addToCart">In den Warenkorb</button>
              <a
                class="btn btn-outline-primary"
                data-bs-toggle="modal"
                href="#requestModal"
              >
                Unverbindliche Anfrage
              </a>
            </td>
          </tr>
          </tfoot>
        </table>
      </div>
    </div>
  </div>
</template>
<style scoped>
.product-main-image img {
  display: block;
  position: absolute;
}
</style>
